import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getConsentState, setConsentState, COOKIE_PREFS_KEY } from '../utils/consent-utils';
import api from '../services/api';
import CookiePreferencesModal from './CookiePreferencesModal';
import './CookieBanner.css';

/**
 * localStorage key used to cache the geo-detection result.
 * Cache is valid for 24 hours to prevent excessive API calls and banner flicker.
 * @constant {string}
 */
const GEO_CACHE_KEY = 'cogslog_geo_cache';

/**
 * Geo cache TTL in milliseconds (24 hours).
 * @constant {number}
 */
const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Read a valid geo result from localStorage cache.
 *
 * Returns null if the cache is absent or expired.
 *
 * @returns {{is_eu: boolean, country_code: string}|null}
 */
function readGeoCache() {
  try {
    const raw = localStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.timestamp > GEO_CACHE_TTL_MS) return null;
    return { is_eu: cached.is_eu, country_code: cached.country_code };
  } catch {
    return null;
  }
}

/**
 * Write a geo result to localStorage cache with the current timestamp.
 *
 * @param {{is_eu: boolean, country_code: string}} geoResult
 */
function writeGeoCache(geoResult) {
  try {
    localStorage.setItem(
      GEO_CACHE_KEY,
      JSON.stringify({ ...geoResult, timestamp: Date.now() })
    );
  } catch {
    // localStorage unavailable — skip caching, banner may flicker
  }
}

/**
 * CookieBanner — fixed-bottom consent banner for EU/UK visitors.
 *
 * Self-managing: reads geo detection and stored preferences on mount,
 * shows only when required (EU/UK visitor, no stored preferences yet).
 *
 * Provides three actions:
 * - Accept All: grants analytics + marketing consent
 * - Reject All: denies analytics + marketing consent
 * - Manage Preferences: opens CookiePreferencesModal for per-category control
 *
 * When the user is authenticated, preferences are also synced to the database
 * via POST /api/cookie-preferences.
 *
 * Geo result is cached in localStorage for 24h to prevent flicker on page load.
 *
 * @returns {React.ReactElement|null} Banner (visible) or null (hidden)
 *
 * @example
 * // Render once at the app root — it self-manages visibility
 * <CookieBanner />
 */
function CookieBanner() {
  const { user } = useAuth();

  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPrefs, setCurrentPrefs] = useState({
    analytics: false,
    marketing: false,
  });

  // On mount: determine whether to show the banner
  useEffect(() => {
    // If the user has already set preferences, never show the banner
    const existingPrefs = localStorage.getItem(COOKIE_PREFS_KEY);
    if (existingPrefs) {
      return;
    }

    // Check geo cache first to avoid flicker
    const cached = readGeoCache();
    if (cached !== null) {
      if (cached.is_eu) {
        setVisible(true);
      }
      return;
    }

    // No valid cache — call the geo API
    api.geoCheck()
      .then((response) => {
        const result = response.data;
        writeGeoCache(result);
        if (result.is_eu) {
          setVisible(true);
        }
      })
      .catch(() => {
        // On geo error, default to not showing the banner (fail-safe for non-EU)
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for the custom 'open-cookie-preferences' event dispatched by AppFooter
  useEffect(() => {
    function handleOpenPreferences() {
      const state = getConsentState();
      setCurrentPrefs({ analytics: state.analytics, marketing: state.marketing });
      setModalOpen(true);
    }

    window.addEventListener('open-cookie-preferences', handleOpenPreferences);
    return () => window.removeEventListener('open-cookie-preferences', handleOpenPreferences);
  }, []);

  /**
   * Save preferences to localStorage (and DB if authenticated), then hide banner.
   *
   * @param {{necessary: boolean, analytics: boolean, marketing: boolean}} prefs
   */
  function saveAndHide(prefs) {
    setConsentState(prefs);

    if (user) {
      api.saveCookiePreferences({ analytics: prefs.analytics, marketing: prefs.marketing })
        .catch(() => {
          // Non-fatal: localStorage save already succeeded
        });
    }

    setVisible(false);
    setModalOpen(false);
  }

  /**
   * Handle "Accept All" button click.
   * Grants consent for all optional cookie categories.
   */
  function handleAcceptAll() {
    saveAndHide({ necessary: true, analytics: true, marketing: true });
  }

  /**
   * Handle "Reject All" button click.
   * Denies all optional cookie categories; necessary remains on.
   */
  function handleRejectAll() {
    saveAndHide({ necessary: true, analytics: false, marketing: false });
  }

  /**
   * Handle "Manage Preferences" button click.
   * Opens the per-category preferences modal.
   */
  function handleManage() {
    const state = getConsentState();
    setCurrentPrefs({ analytics: state.analytics, marketing: state.marketing });
    setModalOpen(true);
  }

  /**
   * Handle save from CookiePreferencesModal.
   * @param {{necessary: boolean, analytics: boolean, marketing: boolean}} prefs
   */
  function handleModalSave(prefs) {
    saveAndHide(prefs);
  }

  // Render nothing when not visible (non-EU, already decided, or still loading)
  if (!visible) {
    return (
      <CookiePreferencesModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
        initialPrefs={currentPrefs}
      />
    );
  }

  return (
    <>
      <div
        className="cookie-banner"
        role="region"
        aria-label="Cookie consent"
        aria-live="polite"
      >
        <div className="cookie-banner__inner">
          <p className="cookie-banner__text">
            We use cookies to improve your experience.{' '}
            Necessary cookies are always active. You can manage your preferences below.{' '}
            <a
              href="https://cogslog.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </p>

          <div className="cookie-banner__actions">
            {/* Equal-weight buttons — both must be the same visual size */}
            <button
              type="button"
              className="btn btn-primary cookie-banner__btn--accept"
              onClick={handleAcceptAll}
            >
              Accept All
            </button>

            <button
              type="button"
              className="btn btn-secondary cookie-banner__btn--reject"
              onClick={handleRejectAll}
            >
              Reject All
            </button>

            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleManage}
            >
              Manage Preferences
            </button>
          </div>
        </div>
      </div>

      <CookiePreferencesModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
        initialPrefs={currentPrefs}
      />
    </>
  );
}

export default CookieBanner;
